import React, { useMemo, useRef } from "react";
import {
  ChatProvider as OriginalProvider,
  ChatProviderProps,
  ChatProviderConfig,
  IChatService,
} from "@chatscope/use-chat";
import { AutoDraft } from "@chatscope/use-chat/dist/enums";

// Default config values
const DEFAULT_CONFIG: ChatProviderConfig = {
  typingThrottleTime: 250,
  debounceTyping: true,
  typingDebounceTime: 900,
  autoDraft: AutoDraft.Save | AutoDraft.Restore,
};

// Make config optional in the wrapper
export function PatchedChatProvider<S extends IChatService>(
  props: Omit<ChatProviderProps<S>, "config"> & { config?: ChatProviderConfig }
) {
  const { config, serviceFactory, ...rest } = props;

  // Merge defaults with user config (stable reference)
  const mergedConfig: ChatProviderConfig = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...(config || {}) }),
    [config]
  );

  /**
   * IMPORTANT:
   * Chatscope's ChatProvider does NOT recreate the service when serviceFactory changes.
   * We explicitly remount it when the factory identity changes.
   */
  const factoryKeyRef = useRef(0);
  const lastFactoryRef = useRef(serviceFactory);

  if (lastFactoryRef.current !== serviceFactory) {
    lastFactoryRef.current = serviceFactory;
    factoryKeyRef.current += 1;
  }

  return (
    <OriginalProvider
      key={factoryKeyRef.current}
      {...(rest as ChatProviderProps<S>)}
      serviceFactory={serviceFactory}
      config={mergedConfig}
    />
  );
}
